import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { api, ApiError } from "@/lib/api";
import type { BDRSummary, Submission, ExtractedPDF, SubmissionResult } from "@shared/types";
import { IdentityBanner } from "@/components/IdentityBanner";
import { BDRPicker } from "@/components/BDRPicker";
import { ChoiceScreen } from "@/components/ChoiceScreen";
import { PDFUploader } from "@/components/PDFUploader";
import { SubmissionForm } from "@/components/SubmissionForm";
import { SubmittingScreen } from "@/components/SubmittingScreen";
import { Confirmation } from "@/components/Confirmation";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Logo, LogoNegative, LogoIcon } from "@/components/Logo";
import { Trophy } from "lucide-react";

// Lazy-load the heavy 3D hero so it doesn't block initial paint
const HeroScene = lazy(() =>
  import("@/components/HeroScene").then((m) => ({ default: m.HeroScene }))
);

type Stage =
  | { kind: "loading" }
  | { kind: "pick"; bdrs: BDRSummary[] }
  | { kind: "choice"; bdr: BDRSummary; bdrs: BDRSummary[] }
  | { kind: "upload"; bdr: BDRSummary; bdrs: BDRSummary[] }
  | { kind: "form"; bdr: BDRSummary; bdrs: BDRSummary[]; prefill?: Partial<Submission>; extractionConfidence?: Record<string, number> }
  | { kind: "submitting"; bdr: BDRSummary; bdrs: BDRSummary[] }
  | { kind: "done"; bdr: BDRSummary; bdrs: BDRSummary[]; result: SubmissionResult }
  | { kind: "error"; message: string };

const REMEMBER_KEY = "bdr_portal_selected_bdr_id";

// Stages that use the dark immersive background
const DARK_STAGES = new Set(["choice", "upload", "form", "submitting", "done"]);

export function Portal() {
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const [error, setError] = useState<string | null>(null);
  const choiceRef = useRef<HTMLDivElement>(null);

  // Load BDR roster on mount
  useEffect(() => {
    api.listBdrs()
      .then(({ bdrs }) => {
        if (bdrs.length === 0) {
          setStage({ kind: "error", message: "No active BDRs configured. Contact ops." });
          return;
        }
        const remembered = sessionStorage.getItem(REMEMBER_KEY);
        const restored = remembered ? bdrs.find((b) => b.id === remembered) : undefined;
        if (restored) {
          setStage({ kind: "choice", bdr: restored, bdrs });
        } else {
          setStage({ kind: "pick", bdrs });
        }
      })
      .catch((e: ApiError) => {
        setStage({
          kind: "error",
          message: e.status === 401 || e.status === 403
            ? "Access denied. Sign in via the Cloudflare Access link."
            : "Unable to load BDR roster. Please refresh.",
        });
      });
  }, []);

  function pickBdr(bdr: BDRSummary, bdrs: BDRSummary[]) {
    sessionStorage.setItem(REMEMBER_KEY, bdr.id);
    setStage({ kind: "choice", bdr, bdrs });
  }

  function changeBdr() {
    if ("bdrs" in stage) {
      sessionStorage.removeItem(REMEMBER_KEY);
      setStage({ kind: "pick", bdrs: stage.bdrs });
    }
  }

  async function handlePDF(extracted: ExtractedPDF) {
    if (stage.kind !== "upload") return;
    setStage({
      kind: "form",
      bdr: stage.bdr,
      bdrs: stage.bdrs,
      prefill: extracted.submission,
      extractionConfidence: extracted.field_confidence,
    });
  }

  async function handleSubmit(submission: Submission) {
    if (stage.kind !== "form") return;
    setStage({ kind: "submitting", bdr: stage.bdr, bdrs: stage.bdrs });
    setError(null);
    try {
      const result = await api.submit({ ...submission, bdr_id: stage.bdr.id });
      setStage({ kind: "done", bdr: stage.bdr, bdrs: stage.bdrs, result });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Submission failed. Please try again.");
      setStage({ kind: "form", bdr: stage.bdr, bdrs: stage.bdrs });
    }
  }

  const isDark = DARK_STAGES.has(stage.kind);

  const showDarkChrome = isDark || stage.kind === "pick";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Gradient accent bar */}
      <div className={`h-[2px] w-full shrink-0 ${showDarkChrome ? "bg-gradient-to-r from-[#05FAFC]/40 via-[#7573F7]/60 to-[#C3A0F2]/40" : "bg-brand-gradient"}`} />

      {/* Header — adapts to dark/light context */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl transition-colors duration-300 ${
        showDarkChrome
          ? "bg-[#0A0118]/80 border-b border-white/[0.06]"
          : "bg-white/80 border-b border-[rgba(61,33,92,0.08)]"
      }`}>
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          {showDarkChrome ? (
            <a href="/" className="shrink-0">
              <LogoNegative height={26} />
            </a>
          ) : (
            <Logo height={28} />
          )}
          <div className="flex items-center gap-2.5">
            <a
              href="/leaderboard"
              className={`text-xs font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
                showDarkChrome
                  ? "text-white/50 hover:text-white/80 hover:bg-white/[0.05]"
                  : "text-grape hover:bg-[rgba(117,115,247,0.06)]"
              }`}
            >
              <Trophy size={13} className={showDarkChrome ? "text-mustard/70" : "text-mustard"} />
              <span className="hidden sm:inline">Leaderboard</span>
            </a>
            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest ${
              showDarkChrome
                ? "bg-white/[0.06] text-white/30 border border-white/[0.06]"
                : "bg-periwinkle/10 text-periwinkle"
            }`}>
              BDR Portal
            </span>
          </div>
        </div>
      </header>

      {/* Dark immersive section for the pick stage (Hero + BDR Picker) */}
      {stage.kind === "pick" && (
        <div className="bg-[#0A0118] relative">
          <Suspense fallback={null}>
            <HeroScene
              onScrollDown={() =>
                choiceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            />
          </Suspense>
          <div ref={choiceRef}>
            <BDRPicker bdrs={stage.bdrs} onConfirm={(bdr) => pickBdr(bdr, stage.bdrs)} />
          </div>
          {/* Fade from dark to light page bg */}
          <div className="h-24 bg-gradient-to-b from-[#0A0118] to-[#F8F6FC]" />
        </div>
      )}

      {/* Dark immersive background for choice/form/upload/submitting stages */}
      {isDark && (
        <div className="flex-1 bg-[#0A0118] relative">
          {/* Ambient background glow */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-500/[0.06] rounded-full filter blur-[128px]" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/[0.06] rounded-full filter blur-[128px]" />
          </div>

          <div className="relative z-10 max-w-2xl mx-auto px-4 py-8 pb-12">
            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            {stage.kind === "choice" && (
              <>
                <IdentityBanner bdr={stage.bdr} onChangeBdr={changeBdr} dark />
                <ChoiceScreen
                  onUpload={() => setStage({ kind: "upload", bdr: stage.bdr, bdrs: stage.bdrs })}
                  onManual={() => setStage({ kind: "form", bdr: stage.bdr, bdrs: stage.bdrs })}
                />
              </>
            )}

            {stage.kind === "upload" && (
              <>
                <IdentityBanner bdr={stage.bdr} onChangeBdr={changeBdr} dark />
                <PDFUploader
                  bdrId={stage.bdr.id}
                  onExtracted={handlePDF}
                  onSwitchManual={() => setStage({ kind: "form", bdr: stage.bdr, bdrs: stage.bdrs })}
                />
              </>
            )}

            {stage.kind === "form" && (
              <>
                <IdentityBanner bdr={stage.bdr} onChangeBdr={changeBdr} dark />
                <SubmissionForm
                  bdr={stage.bdr}
                  prefill={stage.prefill}
                  confidence={stage.extractionConfidence}
                  onSubmit={handleSubmit}
                />
              </>
            )}

            {stage.kind === "submitting" && <SubmittingScreen />}

            {stage.kind === "done" && (
              <>
                <IdentityBanner bdr={stage.bdr} onChangeBdr={changeBdr} dark />
                <Confirmation
                  bdr={stage.bdr}
                  result={stage.result}
                  onSubmitAnother={() => setStage({ kind: "choice", bdr: stage.bdr, bdrs: stage.bdrs })}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Light background for non-dark stages */}
      {!isDark && stage.kind !== "pick" && (
        <main className="flex-1 flex flex-col items-center px-4 py-8 pb-12">
          <div className="w-full max-w-2xl">
            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            {stage.kind === "loading" && (
              <div className="card p-8 text-center text-grape animate-pulse">
                Loading…
              </div>
            )}

            {stage.kind === "error" && (
              <div className="card p-8 text-center">
                <div className="text-2xl font-extrabold text-midnight mb-3">Access not available</div>
                <p className="text-grape">{stage.message}</p>
              </div>
            )}

            {/* done stage now renders in the dark section above */}
          </div>
        </main>
      )}

      {/* Footer */}
      <footer className={`border-t transition-colors duration-300 ${
        showDarkChrome
          ? "border-white/[0.04] bg-[#0A0118]"
          : "border-[rgba(61,33,92,0.06)] bg-white/50 backdrop-blur-sm"
      }`}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <LogoIcon size={16} />
            <span className={`text-xs font-medium ${showDarkChrome ? "text-white/30" : "text-grape"}`}>Channelscaler</span>
            <span className={`text-[11px] ${showDarkChrome ? "text-white/15" : "text-muted-purple"}`}>&middot; Powering Partnerships</span>
          </div>
          <span className={`text-[10px] ${showDarkChrome ? "text-white/15" : "text-muted-purple"}`}>
            All submissions logged &amp; audited &middot; &copy; {new Date().getFullYear()} Channelscaler
          </span>
        </div>
      </footer>
    </div>
  );
}
